import React, {useCallback, useMemo} from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import ArchiveIcon from "@material-ui/icons/Archive";
import {formatDate} from "common/utils/format_date";
import {Definition, DefinitionConfig} from "common/flowmaster/types/data_asset";

const styles = {
  menuItem: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  },
  menuItemName: {
    flexGrow: 1,
  },
  menuItemDate: {
    fontSize: "0.75rem",
    color: "#75757A",
    alignSelf: "center",
    display: "flex",
    alignItems: "center",
  },
  selectedValue: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  },
  selectedValueDate: {
    fontSize: "0.85rem",
    color: "#75757A",
    display: "flex",
    alignItems: "center",
  },
  unsavedText: {
    fontSize: "0.85rem",
    color: "#ff9800",
    fontStyle: "italic",
  },
  archivedText: {
    fontSize: "0.75rem",
    color: "#f44336",
    fontStyle: "italic",
    marginLeft: "8px",
  },
  actionButton: {
    height: 56,
    minWidth: 56,
    width: 56,
    padding: 0,
    borderRadius: 4,
    marginLeft: ".5em",
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  archiveIcon: {
    fontSize: "1rem",
    color: "#757575",
    marginLeft: "4px",
  },
} as const;

interface ConfigurationGroup {
  groupId: string;
  latest: DefinitionConfig;
  items: DefinitionConfig[];
}

interface ConfigurationSelectorProps {
  selectedConfigId: number | string;
  configurations: DefinitionConfig[];
  selectedDefinition?: Definition;
  handleConfigChange: (configId: number) => void;
  handleCreateNew: () => void;
  canCreateNew: boolean;
  formControlStyles?: {
    group: React.CSSProperties;
    version: React.CSSProperties;
  };
  fullWidth?: boolean;
  draftMode?: boolean;
  draftName?: string;
  disabled?: boolean;
}

const ConfigurationSelector: React.FC<ConfigurationSelectorProps> = ({
  selectedConfigId,
  configurations,
  selectedDefinition,
  handleConfigChange,
  handleCreateNew,
  canCreateNew,
  formControlStyles,
  fullWidth,
  draftMode = false,
  draftName = "",
  disabled = false,
}) => {
  const selectedConfig = useMemo(() => {
    return configurations.find(
      (config: DefinitionConfig) => config.id === Number(selectedConfigId),
    );
  }, [configurations, selectedConfigId]);

  const configurationsForGrouping = useMemo(() => {
    const activeConfigs = configurations.filter(
      config => !config.is_archived && config.name !== null,
    );

    if (selectedConfig?.is_archived) {
      return [...activeConfigs, selectedConfig];
    }

    return activeConfigs;
  }, [configurations, selectedConfig]);

  const configurationGroups = useMemo((): ConfigurationGroup[] => {
    const grouped = configurationsForGrouping.reduce(
      (acc: Record<string, DefinitionConfig[]>, config: DefinitionConfig) => {
        const groupId = config.group_id || config.id.toString();
        if (!acc[groupId]) {
          acc[groupId] = [];
        }
        acc[groupId].push(config);
        return acc;
      },
      {} as Record<string, DefinitionConfig[]>,
    );

    Object.keys(grouped).forEach(groupId => {
      grouped[groupId].sort(
        (a, b) =>
          new Date(b.creation_date || 0).getTime() -
          new Date(a.creation_date || 0).getTime(),
      );
    });

    return Object.entries(grouped)
      .map(([groupId, items]: [string, DefinitionConfig[]]) => ({
        groupId,
        latest: items[0],
        items,
      }))
      .sort((a, b) => (a.latest.name || "").localeCompare(b.latest.name || ""));
  }, [configurationsForGrouping]);

  const currentGroup = useMemo(() => {
    if (!selectedConfig) {
      return null;
    }

    const configGroupId =
      selectedConfig.group_id || selectedConfig.id.toString();

    const group = configurationGroups.find(g => g.groupId === configGroupId);
    if (group) {
      return group;
    }

    const groupConfigs = configurations.filter(config => {
      const id = config.group_id || config.id.toString();
      return id === configGroupId;
    });

    groupConfigs.sort(
      (a, b) =>
        new Date(b.creation_date || 0).getTime() -
        new Date(a.creation_date || 0).getTime(),
    );

    return {
      groupId: configGroupId,
      latest: groupConfigs[0],
      items: groupConfigs,
    };
  }, [configurationGroups, selectedConfig, configurations]);

  const handleGroupChange = useCallback(
    (_: React.ChangeEvent<object>, newValue: ConfigurationGroup | null) => {
      if (newValue) {
        handleConfigChange(newValue.latest.id);
      }
    },
    [handleConfigChange],
  );

  const handleVersionChange = useCallback(
    (event: React.ChangeEvent<{value: unknown}>) => {
      const configId = event.target.value as number;
      handleConfigChange(configId);
    },
    [handleConfigChange],
  );

  const renderVersionSelectValue = useCallback(
    selected => {
      if (draftMode) {
        return (
          <div style={styles.selectedValue}>
            <span>New Version</span>
            <span style={styles.unsavedText}>(Unsaved)</span>
          </div>
        );
      }

      const config = configurations.find(c => c.id === Number(selected));
      if (!config) {
        return "";
      }

      return (
        <div style={styles.selectedValue}>
          <span>{formatDate(config.creation_date)}</span>
          {config.is_archived && <ArchiveIcon style={styles.archiveIcon} />}
        </div>
      );
    },
    [configurations, draftMode],
  );

  return (
    <>
      {draftMode ? (
        <TextField
          label="Select Configuration"
          value={draftName}
          variant="outlined"
          style={formControlStyles?.group}
          disabled
        />
      ) : (
        <Autocomplete
          style={formControlStyles?.group}
          fullWidth={fullWidth}
          options={configurationGroups}
          getOptionLabel={group => group.latest.name}
          value={currentGroup}
          onChange={handleGroupChange}
          disabled={
            disabled || !selectedDefinition || configurationGroups.length === 0
          }
          renderInput={params => (
            <TextField
              {...params}
              label="Select Configuration"
              variant="outlined"
            />
          )}
          renderOption={group => (
            <div style={styles.menuItem}>
              <span style={styles.menuItemName}>{group.latest.name}</span>
              <span style={styles.menuItemDate}>
                {group.items.length} version
                {group.items.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        />
      )}
      <FormControl
        variant="outlined"
        style={formControlStyles?.version}
        fullWidth={fullWidth}
      >
        <InputLabel>Version</InputLabel>
        <Select
          value={draftMode ? "draft" : selectedConfigId}
          onChange={handleVersionChange}
          label="Version"
          disabled={
            draftMode || !currentGroup || currentGroup.items.length === 0
          }
          renderValue={renderVersionSelectValue}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                overflowY: "auto",
              },
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            getContentAnchorEl: null,
          }}
        >
          {currentGroup?.items.map(config => (
            <MenuItem key={config.id} value={config.id}>
              <div style={styles.menuItem}>
                <span>{formatDate(config.creation_date)}</span>
                {config.is_archived && (
                  <ArchiveIcon style={styles.archiveIcon} />
                )}
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!draftMode && canCreateNew && (
        <Button
          variant="contained"
          color="primary"
          style={styles.actionButton}
          onClick={handleCreateNew}
          disabled={disabled || !selectedDefinition}
          title={"Create new configuration"}
        >
          <div style={styles.iconContainer}>
            <AddIcon />
          </div>
        </Button>
      )}
    </>
  );
};

export default ConfigurationSelector;
